import { Button, Modal } from 'antd'
import { LeftOutlined, PlusOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Dayjs } from 'dayjs'
import { v4 as uuidv4 } from 'uuid'
import { ScheduledEvent, SchedulingJob } from '../scheduleApi'
import { useCreateScheduledEventsByJobV2Mutation, useRescheduleEventMutation, useSchedulingWorkAreasQuery } from '../scheduleQueries'
import { useSchedule } from '../ScheduleContext'
import CadenceSection, { CadenceSectionHandle } from './CadenceSection'
import { buildCreateEventsV2Payload, buildRescheduleNewEvent, CadenceData, ExpandedEvent, expandCadenceEvents } from './createEventPayloadBuilder'
import { CadenceFormData } from '../EventForm/recurringScheduleModel'
import SchedulePreviewTable from './SchedulePreviewTable'

type Props = {
  open: boolean
  onClose: () => void
  job: SchedulingJob | null
  onSuccess?: () => void
  selectedDate?: Dayjs
  selectedTime?: number
  selectedDuration?: number
  initialEvent?: ScheduledEvent
  rescheduleFromEventId?: number
}

const CreateEventFromJobModal = ({ open, onClose, job, onSuccess, selectedDate: initialDate, selectedTime, selectedDuration, initialEvent, rescheduleFromEventId }: Props) => {
  const isReschedule = rescheduleFromEventId !== undefined

  const [cadenceKeys, setCadenceKeys] = useState<number[]>([0])
  const [nextKey, setNextKey] = useState(1)
  const [collapsedKeys, setCollapsedKeys] = useState<Set<number>>(new Set())
  const [isSaving, setIsSaving] = useState(false)
  const [step, setStep] = useState<1 | 2>(1)
  const [expandedEvents, setExpandedEvents] = useState<ExpandedEvent[]>([])
  const [cadenceDataList, setCadenceDataList] = useState<CadenceData[]>([])
  const [cadenceFormDataMap, setCadenceFormDataMap] = useState<Map<number, CadenceFormData>>(new Map())
  const nextKeyRef = useRef(0)
  const cadenceRefs = useRef<Map<number, CadenceSectionHandle>>(new Map())

  const createEventsV2Mutation = useCreateScheduledEventsByJobV2Mutation()
  const rescheduleEventMutation = useRescheduleEventMutation()
  const { crew } = useSchedule()
  const { data: workAreas } = useSchedulingWorkAreasQuery(job?.jobId || 0)

  const allSelectedWorkareaIds = useMemo(() => {
    return new Set([...cadenceFormDataMap.values()].flatMap(data => data.formValues.selectedWorkareaIds))
  }, [cadenceFormDataMap])

  const isAnyCadenceRecurring = useMemo(() => {
    return [...cadenceFormDataMap.values()].some(data => data.formValues.repeatUnit !== 'none')
  }, [cadenceFormDataMap])

  const willCreateMultipleEvents = cadenceKeys.length > 1 || isAnyCadenceRecurring

  const isBusy = isSaving || createEventsV2Mutation.isLoading || rescheduleEventMutation.isLoading

  const scheduledCount = workAreas?.filter(w => w.scheduledEventCount > 0 || allSelectedWorkareaIds.has(w.id)).length ?? 0
  const unscheduledCount = workAreas?.filter(w => w.scheduledEventCount === 0 && !allSelectedWorkareaIds.has(w.id)).length ?? 0

  const allCollapsed = cadenceKeys.length > 0 && cadenceKeys.every(key => collapsedKeys.has(key))

  useEffect(() => {
    if (open) {
      setCadenceKeys([0])
      setNextKey(1)
      setCollapsedKeys(new Set())
      setCadenceFormDataMap(new Map())
      setStep(1)
      setExpandedEvents([])
      setCadenceDataList([])
    }
  }, [open])

  const handleCadenceFormDataChange = (key: number) => (data: CadenceFormData) => {
    setCadenceFormDataMap(prev => {
      const next = new Map(prev)
      next.set(key, data)
      return next
    })
  }

  const handleAddCadence = () => {
    setCollapsedKeys(new Set(cadenceKeys))
    setCadenceKeys(prev => [...prev, nextKey])
    setNextKey(prev => prev + 1)
  }

  const handleRemoveCadence = (key: number) => {
    setCadenceKeys(prev => prev.filter(k => k !== key))
    cadenceRefs.current.delete(key)
    setCadenceFormDataMap(prev => {
      const next = new Map(prev)
      next.delete(key)
      return next
    })
    setCollapsedKeys(prev => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
  }

  const handleCadenceCollapsedChange = (key: number) => (collapsed: boolean) => {
    setCollapsedKeys(prev => {
      const next = new Set(prev)
      if (collapsed) {
        next.add(key)
      } else {
        next.delete(key)
      }
      return next
    })
  }

  const handleToggleCollapseAll = () => {
    if (allCollapsed) {
      setCollapsedKeys(new Set())
    } else {
      setCollapsedKeys(new Set(cadenceKeys))
    }
  }

  const handleClose = () => {
    setCadenceKeys([0])
    setNextKey(1)
    setCollapsedKeys(new Set())
    setCadenceFormDataMap(new Map())
    setStep(1)
    setExpandedEvents([])
    setCadenceDataList([])
    onClose()
  }

  const validateAllCadences = async (): Promise<boolean> => {
    for (const key of cadenceKeys) {
      const ref = cadenceRefs.current.get(key)
      if (!ref) {
        return false
      }
      const valid = await ref.validate()
      if (!valid) {
        return false
      }
    }
    return true
  }

  const buildCadencesFromForm = (): { cadences: CadenceData[], events: ExpandedEvent[] } | null => {
    nextKeyRef.current = 0
    const allEvents: ExpandedEvent[] = []
    const cadences: CadenceData[] = []

    for (const key of cadenceKeys) {
      const formData = cadenceFormDataMap.get(key)
      if (!formData) {
        return null
      }

      const events = expandCadenceEvents(key, formData, nextKeyRef)

      cadences.push({
        cadenceKey: key,
        repeatingId: formData.repeatingEventConfig ? uuidv4() : null,
        repeatingEventConfig: formData.repeatingEventConfig,
        workareaIds: formData.formValues.selectedWorkareaIds.join(','),
        sendCrewLeadNotifications: formData.formValues.sendCrewLeadNotifications,
        events,
      })

      allEvents.push(...events)
    }

    return { cadences, events: allEvents }
  }

  const handleMutationSuccess = () => {
    if (onSuccess) {
      onSuccess()
    }
    handleClose()
  }

  const saveCadences = (cadences: CadenceData[]) => {
    if (!job) {
      return
    }

    if (isReschedule) {
      const newEvent = buildRescheduleNewEvent(job, cadences)
      if (!newEvent || rescheduleFromEventId === undefined) {
        return
      }
      rescheduleEventMutation.mutate(
        { originalEventId: rescheduleFromEventId, newEvent },
        { onSuccess: handleMutationSuccess }
      )
      return
    }

    const payload = buildCreateEventsV2Payload(job, cadences)
    if (!payload) {
      return
    }

    createEventsV2Mutation.mutate(payload, { onSuccess: handleMutationSuccess })
  }

  const handleNext = async () => {
    if (!job) {
      return
    }

    const valid = await validateAllCadences()
    if (!valid) {
      return
    }

    const result = buildCadencesFromForm()
    if (!result) {
      return
    }

    setExpandedEvents(result.events)
    setCadenceDataList(result.cadences)
    setStep(2)
  }

  const handleSaveFromForm = async () => {
    if (!job) {
      return
    }

    setIsSaving(true)
    try {
      const valid = await validateAllCadences()
      if (!valid) {
        return
      }

      const result = buildCadencesFromForm()
      if (!result) {
        return
      }

      saveCadences(result.cadences)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveFromPreview = () => {
    if (cadenceDataList.length === 0) {
      return
    }

    const updatedCadences = cadenceDataList.map(c => ({
      ...c,
      events: expandedEvents.filter(e => e.cadenceKey === c.cadenceKey),
    }))

    saveCadences(updatedCadences)
  }

  const handlePrimaryAction = async () => {
    if (willCreateMultipleEvents) {
      await handleNext()
      return
    }
    await handleSaveFromForm()
  }

  const setCadenceRef = (key: number) => (el: CadenceSectionHandle | null) => {
    if (el) {
      cadenceRefs.current.set(key, el)
    } else {
      cadenceRefs.current.delete(key)
    }
  }

  const cadenceWorkareaMap = useMemo(() => {
    const map = new Map<number, number[]>()
    cadenceFormDataMap.forEach((data, key) => map.set(key, data.formValues.selectedWorkareaIds))
    return map
  }, [cadenceFormDataMap])

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={
        step === 1 ? (
          <div style={{ borderTop: '1px solid #f0f0f0', margin: '0 -16px', padding: '16px 16px 0' }}>
            {!isReschedule && (
              <div style={{ padding: '0 0 16px' }}>
                <Button
                  block
                  icon={<PlusOutlined />}
                  onClick={handleAddCadence}
                >
                  Add Another
                </Button>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={handleClose}>Cancel</Button>
              <Button type="primary" onClick={handlePrimaryAction} disabled={isBusy} loading={isBusy}>
                {isReschedule ? 'Reschedule' : willCreateMultipleEvents ? 'Review Schedule' : 'Schedule Job'}
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button icon={<LeftOutlined />} onClick={() => setStep(1)}>
              Back
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={handleClose}>Cancel</Button>
              <Button type="primary" onClick={handleSaveFromPreview} disabled={isBusy || expandedEvents.length === 0} loading={isBusy}>
                Schedule Job
              </Button>
            </div>
          </div>
        )
      }
      width={step === 2 ? 1100 : 700}
      title={<ModalTitle job={job} step={step} isReschedule={isReschedule} />}
      style={{ top: '5vh' }}
      styles={{
        body: { maxHeight: 'calc(90vh - 200px)', overflowY: 'auto', padding: step === 1 ? 0 : undefined },
        header: { padding: '16px 24px', margin: 0 },
        footer: { padding: '24px 16px', margin: 0 },
        content: { padding: step === 1 ? 0 : undefined },
      }}
    >
      <div style={{ display: step === 1 ? undefined : 'none' }}>
        <div style={{ padding: '16px 24px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <a onClick={handleToggleCollapseAll} style={{ color: '#fa541c', fontSize: 14 }}>
              {allCollapsed ? 'Expand All' : 'Collapse All'}
            </a>
            <span style={{ color: '#666', fontSize: 13 }}>
              {scheduledCount} Scheduled Work Areas • {unscheduledCount} Unscheduled Work Areas
            </span>
          </div>
        </div>

        {job && cadenceKeys.map((key, index) => (
          <CadenceSection
            key={key}
            ref={setCadenceRef(key)}
            number={index + 1}
            job={job}
            defaultDate={initialDate}
            defaultTimeMinutes={index === 0 ? selectedTime : undefined}
            defaultDurationMinutes={index === 0 ? selectedDuration : undefined}
            initialEvent={index === 0 ? initialEvent : undefined}
            canRemove={cadenceKeys.length > 1}
            collapsed={collapsedKeys.has(key)}
            onCollapsedChange={handleCadenceCollapsedChange(key)}
            onRemove={() => handleRemoveCadence(key)}
            onFormDataChange={handleCadenceFormDataChange(key)}
            open={open}
            hideRepeatSelector={isReschedule}
          />
        ))}
      </div>
      {step === 2 && (
        <SchedulePreviewTable
          events={expandedEvents}
          onEventsChange={setExpandedEvents}
          crew={crew || []}
          workAreas={workAreas || []}
          cadenceWorkareaMap={cadenceWorkareaMap}
        />
      )}
    </Modal>
  )
}

const ModalTitle = ({ job, step, isReschedule }: { job: SchedulingJob | null, step?: number, isReschedule?: boolean }) => (
  <div style={{ paddingRight: '24px' }}>
    <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <span>{job?.jobName || 'Job Name'}</span>
      {job?.jobId ? (
        <FontAwesomeIcon
          icon={faArrowUpRightFromSquare}
          style={{ color: '#1677ff', cursor: 'pointer', fontSize: '12px' }}
          role="button"
          tabIndex={0}
          aria-label="Open job in new tab"
          onClick={() => window.open(`/#!/job-redirect/${job.jobId}`, '_blank')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              window.open(`/#!/job-redirect/${job.jobId}`, '_blank')
            }
          }}
        />
      ) : null}
      {isReschedule && <span style={{ fontWeight: 400, fontSize: '14px', color: '#666' }}>— Reschedule</span>}
      {step === 2 && <span style={{ fontWeight: 400, fontSize: '14px', color: '#666' }}>— Review Schedule</span>}
    </div>
    <div style={{
      fontSize: '12px',
      fontWeight: 400,
      color: '#666',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}>
      {job?.customerDisplayName}
      {job?.address1 && job?.city && job?.state && job?.zip && (
        <> | {job.address1}, {job.city}, {job.state} {job.zip}</>
      )}
    </div>
  </div>
)

export default CreateEventFromJobModal
